// Booking.tsx
import { useState, useEffect } from 'react';
import { ApiService } from '../services/api.service';
import { PersonSelectionModal } from './PersonSelectionModal';
import { DateTimePicker } from './DateTimePicker';
import { ConfirmationModal } from './ConfirmationModal';
import { LoadingSpinner } from './LoadingSpinner';
import { ServiceCategoryResponseDTO, ServiceResponseDTO } from './types/ZaprosDTO';
import { BottomMenu } from './BottomMenu/BottomMenu';
import { useBookingContext } from './context/BookingContext';
import './Booking.css';
import '../styles/variables.css';
import { QuantitySelectionModal } from './QuantitySelectionModal';

type Step = 
  | 'categories' 
  | 'person-select' 
  | 'quantity-select'
  | 'date-select' 
  | 'confirm';

export const Booking = () => {
  const { bookingData, setBookingData } = useBookingContext();
  const [step, setStep] = useState<Step>('categories');
  const [categories, setCategories] = useState<ServiceCategoryResponseDTO[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceResponseDTO | null>(null);
  const [personRanges, setPersonRanges] = useState<Array<{
    service: ServiceResponseDTO;
    description: string; 
    price: number
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await ApiService.getCategories();
        setCategories(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  const handleCategorySelect = async (category: ServiceCategoryResponseDTO) => {
    setLoading(true);
    try {
      const services = await ApiService.getServicesByCategory(category.id);
      const activeServices = services.filter(
        service => service.is_active && service.service_id > 0
      );
      
      if (activeServices.length === 0) throw new Error('Нет доступных услуг');
      
      const ranges = activeServices.map(s => ({
        service: s,
        description: s.description, 
        price: s.price_per_unit
      }));

      setPersonRanges(ranges);
      setSelectedService(activeServices[0]);
      setStep(activeServices[0].person_based_pricing ? 'person-select' : 'quantity-select');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handlePersonSelect = (service: ServiceResponseDTO, price: number, tables: number) => {
    setSelectedService(service);
    
    setBookingData({ 
      ...bookingData,
      service_id: service.service_id,
      quantity: tables,
      object_id: tables,
      total_price: price * tables,
      added_to_cart_time: new Date().toISOString()
    });
    setStep('date-select');
  };

  const handleQuantitySelect = (quantity: number, price: number) => {
    if (!selectedService) return;
    
    setBookingData({
      ...bookingData,
      service_id: selectedService.service_id,
      quantity: quantity,
      total_price: price * quantity,
      added_to_cart_time: new Date().toISOString()
    });
    setStep('date-select');
  };

  const handleDateSelect = (date: string, time: string) => {
    if (!selectedService) return;

    const isPersonBased = selectedService.person_based_pricing;
    
    const startTime = isPersonBased ? '09:00:00' : time;
    const endTime = isPersonBased ? '21:00:00' : calculateEndTime(time);

    const addedToCartTime = new Date(new Date().getTime() + 4 * 60 * 60 * 1000).toISOString();

    setBookingData({
      ...bookingData,
      booking_date: date,
      start_time: `${startTime}`,
      end_time: `${endTime}`,
      added_to_cart_time: addedToCartTime
    });

    setStep('confirm');
  };

  const handleConfirm = async () => {
    try {
      await ApiService.createBooking({
        ...bookingData,
        client_telegram_id: undefined 
      });
      
      alert('Услуга забронирована');
      setStep('categories');
    } catch (err) {
      setError('Ошибка создания бронирования'); 
    }
  };

  const calculateEndTime = (startTime: string) => {
    const [hours] = startTime.split(':');
    return `${(parseInt(hours) + 1).toString().padStart(2, '0')}:00:00`;
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="booking-container">
      {step === 'categories' && (
        <CategoryGrid categories={categories} onSelect={handleCategorySelect} />
      )}

      {step === 'person-select' && selectedService && (
        <PersonSelectionModal
          ranges={personRanges.map(r => ({
            service: r.service,
            description: r.service.description,
            price: r.price
          }))} 
          availableTables={selectedService.available_quantity}
          onSelect={handlePersonSelect}
          onClose={() => setStep('categories')}
          serviceName={selectedService.name}
        />
      )}

      {step === 'quantity-select' && selectedService && (
        <QuantitySelectionModal
          ranges={personRanges}
          onSelect={handleQuantitySelect}
          onClose={() => setStep('categories')}
          serviceName={selectedService.name}
          availableQuantity={selectedService.available_quantity}
        />
      )}

      {step === 'date-select' && selectedService && (
        <DateTimePicker
          showTimePicker={!selectedService.person_based_pricing}
          onConfirm={handleDateSelect}
          onCancel={() => setStep(
            selectedService.person_based_pricing ? 'person-select' : 'categories'
          )}
          selectedService={selectedService}
          bookingQuantity={bookingData.quantity}
        />
      )}

      {step === 'confirm' && selectedService && (
        <ConfirmationModal
          bookingData={bookingData}
          service={selectedService}
          onConfirm={handleConfirm}
          onCancel={() => setStep('date-select')}
        />
      )}

      <BottomMenu/>
    </div>
  );
};

const CategoryGrid = ({ categories, onSelect }: { 
  categories: ServiceCategoryResponseDTO[]; 
  onSelect: (category: ServiceCategoryResponseDTO) => void; 
}) => {
  const [textColor, setTextColor] = useState<string>('#000000'); // Цвет по умолчанию

  // Получение цвета текста из темы Telegram
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      const themeParams = window.Telegram.WebApp.themeParams;
      const newTextColor = themeParams.text_color || '#000000'; // Используем text_color из темы или дефолтный
      setTextColor(newTextColor);
    }
  }, []);

  return (
    <div className="category-grid">
      {categories.map(category => (
        <div 
          key={category.id}
          className="category-card"
          onClick={() => onSelect(category)}
        >
          <div className="category-image-container">
            <img 
              src={category.photo} 
              alt={category.name} 
              className="category-image"
            />
          </div>
          <h3 className="category-title" style={{ color: textColor }}>
            {category.name}
          </h3>
        </div>
      ))}
    </div>
  );
};